'use client';

import * as React from 'react';
import { format } from 'date-fns';
import { fr, enUS, es, it, type Locale } from 'date-fns/locale';
import { useLocale } from 'next-intl';
import { Calendar as CalendarIcon, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

interface DatePickerBirthProps {
  value?: Date | string;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  maxDate?: Date;
  minDate?: Date;
  disabled?: boolean;
}

const localeMap: Record<string, Locale> = {
  fr,
  en: enUS,
  es,
  it,
};

export const DatePickerBirth: React.FC<DatePickerBirthProps> = ({
  value,
  onChange,
  placeholder = 'Sélectionner une date',
  className,
  maxDate,
  minDate,
  disabled = false,
}) => {
  const locale = useLocale();
  const currentLocale = localeMap[locale] || fr;
  
  // Mémoriser maxDate et minDate normalisés pour éviter les recalculs
  const maxDateNormalized = React.useMemo(() => {
    if (!maxDate) return null;
    return new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
  }, [maxDate]);
  
  const minDateNormalized = React.useMemo(() => {
    if (!minDate) return null;
    return new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
  }, [minDate]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
    value ? (typeof value === 'string' ? new Date(value) : value) : undefined
  );
  const [selectedYear, setSelectedYear] = React.useState<number>(
    selectedDate ? selectedDate.getFullYear() : new Date().getFullYear() - 18
  );
  const [selectedMonth, setSelectedMonth] = React.useState<number>(
    selectedDate ? selectedDate.getMonth() : new Date().getMonth()
  );
  const [selectedDay, setSelectedDay] = React.useState<number | undefined>(
    selectedDate ? selectedDate.getDate() : undefined
  );
  const [step, setStep] = React.useState<'year' | 'month' | 'day'>('year');
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Réinitialiser l'étape à l'ouverture du popup
  React.useEffect(() => {
    if (isOpen) setStep('year');
  }, [isOpen]);

  React.useEffect(() => {
    if (value) {
      const date = typeof value === 'string' ? new Date(value) : value;
      setSelectedDate(date);
      setSelectedYear(date.getFullYear());
      setSelectedMonth(date.getMonth());
      setSelectedDay(date.getDate());
    }
  }, [value]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Générer les années disponibles (100 ans en arrière depuis aujourd'hui)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  
  // Utiliser les noms de mois localisés depuis date-fns
  const months = Array.from({ length: 12 }, (_, i) => {
    const date = new Date(2024, i, 1);
    return format(date, 'MMMM', { locale: currentLocale });
  });

  // Générer les jours du mois sélectionné
  const getDaysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const daysInMonth = getDaysInMonth(selectedYear, selectedMonth);
  const firstDayOfMonth = new Date(selectedYear, selectedMonth, 1).getDay();
  // Ajuster pour que lundi = 0
  const adjustedFirstDay = (firstDayOfMonth + 6) % 7;
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const handleDateSelect = (day: number) => {
    // Créer la date avec l'heure à midi pour éviter les problèmes de fuseau horaire
    const newDate = new Date(selectedYear, selectedMonth, day, 12, 0, 0, 0);
    
    // Vérifier les limites
    if (maxDate) {
      const maxDateAtMidnight = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate(), 23, 59, 59);
      if (newDate > maxDateAtMidnight) return;
    }
    if (minDate) {
      const minDateAtMidnight = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate(), 0, 0, 0);
      if (newDate < minDateAtMidnight) return;
    }
    
    setSelectedDay(day);
    setSelectedDate(newDate);
    onChange?.(newDate);
    setIsOpen(false);
  };

  const handleYearSelect = (year: number) => {
    setSelectedYear(year);
    const maxDays = getDaysInMonth(year, selectedMonth);
    if (selectedDay && selectedDay > maxDays) {
      setSelectedDay(undefined);
      setSelectedDate(undefined);
    }
    setStep('month');
  };

  const handleMonthSelect = (month: number) => {
    setSelectedMonth(month);
    const maxDays = getDaysInMonth(selectedYear, month);
    if (selectedDay && selectedDay > maxDays) {
      setSelectedDay(undefined);
      setSelectedDate(undefined);
    }
    setStep('day');
  };

  const dayNames = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={cn(
          'flex h-10 w-full items-center justify-between rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100',
          'hover:bg-gray-50 dark:hover:bg-gray-700',
          'focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent',
          disabled && 'opacity-50 cursor-not-allowed',
          className
        )}
      >
        <span className={cn('flex items-center gap-2', !selectedDate && 'text-gray-500 dark:text-gray-400')}>
          <CalendarIcon className="h-4 w-4" />
          {selectedDate ? format(selectedDate, 'dd/MM/yyyy', { locale: currentLocale }) : placeholder}
        </span>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg p-4 w-80">
          {/* Bouton Retour (mois → année, jour → mois) */}
          {step !== 'year' && (
            <button
              type="button"
              onClick={() => setStep(step === 'day' ? 'month' : 'year')}
              className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-3"
            >
              <ChevronLeft className="h-4 w-4" />
              Retour
            </button>
          )}

          {/* Étape 1 : Année */}
          {step === 'year' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                Année
              </label>
              <div className="grid grid-cols-5 gap-1.5 max-h-64 overflow-y-auto pr-1">
                {years.map((year) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => handleYearSelect(year)}
                    className={cn(
                      'px-2 py-2.5 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-center',
                      selectedYear === year &&
                        'bg-gold text-white font-semibold hover:bg-gold-dark hover:text-white'
                    )}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Étape 2 : Mois */}
          {step === 'month' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                Mois
              </label>
              <div className="grid grid-cols-2 gap-2">
                {months.map((month, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleMonthSelect(index)}
                    className={cn(
                      'px-3 py-2.5 text-sm rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left',
                      selectedMonth === index &&
                        'bg-gold text-white font-semibold hover:bg-gold-dark hover:text-white'
                    )}
                  >
                    {month}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Étape 3 : Jour */}
          {step === 'day' && (
            <div>
              <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                Jour
              </label>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {dayNames.map((day) => (
                  <div
                    key={day}
                    className="text-center text-xs font-medium text-gray-500 dark:text-gray-400 py-1"
                  >
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {Array.from({ length: adjustedFirstDay }).map((_, i) => (
                  <div key={`empty-${i}`} className="h-8" />
                ))}
                {days.map((day) => {
                  const date = new Date(selectedYear, selectedMonth, day, 12, 0, 0, 0);
                  const dateNormalized = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                  let isDisabled = false;
                  if (maxDateNormalized) {
                    isDisabled = dateNormalized.getTime() > maxDateNormalized.getTime();
                  }
                  if (!isDisabled && minDateNormalized) {
                    isDisabled = dateNormalized.getTime() < minDateNormalized.getTime();
                  }
                  const isSelected = selectedDay === day;
                  const isToday =
                    day === new Date().getDate() &&
                    selectedMonth === new Date().getMonth() &&
                    selectedYear === new Date().getFullYear();

                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => {
                        if (!isDisabled) handleDateSelect(day);
                      }}
                      disabled={isDisabled}
                      className={cn(
                        'h-8 w-8 text-sm rounded transition-colors',
                        isSelected && 'bg-gold text-white font-semibold hover:bg-gold-dark hover:text-white',
                        !isSelected && !isDisabled && 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer',
                        isToday && !isSelected && 'bg-gray-100 dark:bg-gray-700 font-semibold',
                        isDisabled && 'text-gray-400 dark:text-gray-500 opacity-50 cursor-not-allowed'
                      )}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
